import { Link } from "react-router-dom";
import { ArrowRight, Pencil, Stamp } from "lucide-react";
import { TOOL_DEFINITIONS } from "@/config/tools";

const EDIT_PDF_TOOL = TOOL_DEFINITIONS.find((t) => t.id === "edit-pdf-visual");
const BATCH_STAMP_TOOL = TOOL_DEFINITIONS.find((t) => t.id === "batch-stamp");
const OTHER_TOOLS = TOOL_DEFINITIONS.filter(
  (t) => t.id !== "edit-pdf-visual" && t.id !== "batch-stamp",
).slice(0, 6);

export const ToolsOverview = () => {
  return (
    <section className="bg-background py-20 md:py-24">
      <div className="mx-auto max-w-6xl px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="font-serifAccent text-3xl tracking-tight text-foreground md:text-4xl">
            Powerful PDF Tools
          </h2>
          <p className="mt-3 text-text-secondary">
            Everything runs locally. Your files never leave your device.
          </p>
        </div>

        {/* Featured Tools Grid */}
        <div className="grid gap-4 md:grid-cols-2 mb-6">
          {/* Featured: Edit PDF */}
          {EDIT_PDF_TOOL && (
            <Link
              to={EDIT_PDF_TOOL.href}
              className="group relative block overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:border-foreground/40 hover:shadow-lg"
            >
              <div className="flex flex-col gap-4">
                {/* Icon */}
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-500 text-white transition-transform duration-300 group-hover:scale-105">
                  <Pencil className="h-6 w-6" />
                </div>

                {/* Content */}
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <h3 className="font-serifAccent text-lg text-foreground">
                      {EDIT_PDF_TOOL.name}
                    </h3>
                    <span className="rounded-full bg-gradient-to-r from-primary/20 to-blue-500/20 px-2 py-0.5 text-[0.6rem] font-medium uppercase tracking-wider text-primary">
                      Popular
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary line-clamp-2">
                    Add text, shapes, highlights, and annotations to your PDFs.
                  </p>
                </div>

                {/* CTA */}
                <div className="flex items-center gap-2 text-sm font-medium text-foreground transition-all duration-300 group-hover:gap-3">
                  <span>Open Editor</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          )}

          {/* Featured: Batch Stamp */}
          {BATCH_STAMP_TOOL && (
            <Link
              to={BATCH_STAMP_TOOL.href}
              className="group relative block overflow-hidden rounded-2xl border border-border p-6 transition-all duration-300 hover:border-primary/40 hover:shadow-lg"
            >
              <div className="flex flex-col gap-4">
                {/* Icon */}
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-500 text-white transition-transform duration-300 group-hover:scale-105">
                  <Stamp className="h-6 w-6" />
                </div>

                {/* Content */}
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <h3 className="font-serifAccent text-lg text-foreground">
                      {BATCH_STAMP_TOOL.name}
                    </h3>
                    <span className="rounded-full bg-gradient-to-r from-primary/20 to-blue-500/20 px-2 py-0.5 text-[0.6rem] font-medium uppercase tracking-wider text-primary">
                      New
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary line-clamp-2">
                    Upload folders, configure once, stamp all. Industry-grade
                    batch processing.
                  </p>
                </div>

                {/* CTA */}
                <div className="flex items-center gap-2 text-sm font-medium text-foreground transition-all duration-300 group-hover:gap-3">
                  <span>Start Batch Processing</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Tools Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {OTHER_TOOLS.map((tool) => (
            <Link
              key={tool.id}
              to={tool.href}
              className="group rounded-xl border border-border bg-card p-6 transition-all duration-200 hover:border-foreground/30 hover:shadow-md"
            >
              <p className="mb-2 text-[0.65rem] font-medium uppercase tracking-widest text-text-secondary/70">
                {tool.categoryLabel}
              </p>
              <h3 className="mb-2 font-medium text-foreground">{tool.name}</h3>
              <p className="mb-4 text-sm text-text-secondary line-clamp-2">
                {tool.description}
              </p>
              <div className="flex items-center gap-2 text-sm text-text-secondary transition-colors group-hover:text-foreground">
                <span>Use tool</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>

        {/* View All */}
        <div className="mt-10 text-center">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-sm font-medium text-text-secondary transition-colors hover:text-foreground"
          >
            View all tools
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
